/**
 * Sanitization utilities for AI-generated output before rendering.
 * Catches leaked template variables, incoherent text, and overly long labels.
 */

// Known verbatim targeting statement that leaks from prompts
const VERBATIM_TARGETING = 'For First-time and early-stage founders aged 22–45 who have a startup idea but lack the framework, experience, or resources to objectively assess its viability before investing time and money.';

// Pattern: "[Category] find existing solutions too [adjective]..."
const CATEGORY_ADJECTIVE_PATTERN = /^(\w+)\s+find existing solutions too\s+(\w+)/i;

// Generic marketplace/product references that indicate contamination
const CONTAMINATION_MARKERS = [
  'facebook marketplace',
  'amazon marketplace',
  'ebay marketplace',
  'etsy marketplace',
  'craigslist',
];

/**
 * Sanitize a single text field from AI output.
 * Returns cleaned text or null if the content should be hidden.
 */
export function sanitizeText(text: string | null | undefined): string | null {
  if (!text || typeof text !== 'string') return null;
  let cleaned = text.trim();

  // Remove verbatim targeting statement
  if (cleaned.includes(VERBATIM_TARGETING) || cleaned.includes('First-time and early-stage founders aged 22')) {
    cleaned = cleaned.replace(VERBATIM_TARGETING, '').trim();
    if (cleaned.length < 10) return null;
  }

  // Fix "Marketplace find existing solutions too [adjective]" pattern
  const catMatch = cleaned.match(CATEGORY_ADJECTIVE_PATTERN);
  if (catMatch) {
    const [, category, adjective] = catMatch;
    cleaned = `${category} users find existing solutions too ${adjective}`;
  }

  // Check for contamination markers
  const lower = cleaned.toLowerCase();
  for (const marker of CONTAMINATION_MARKERS) {
    if (lower.includes(marker)) {
      return null; // Mark as low-confidence
    }
  }

  return cleaned;
}

/**
 * Sanitize text used as a label or tag (must be concise).
 * Truncates to maxWords and adds ellipsis if needed.
 */
export function sanitizeLabel(text: string | null | undefined, maxWords = 15): string | null {
  const cleaned = sanitizeText(text);
  if (!cleaned) return null;

  const words = cleaned.split(/\s+/);
  if (words.length > maxWords) {
    return words.slice(0, maxWords).join(' ') + '…';
  }
  return cleaned;
}

/**
 * Check if a desire/pain point text is incoherent or contaminated.
 * Returns the cleaned text or a fallback message.
 */
export function sanitizeDesire(text: string | null | undefined): string | null {
  const cleaned = sanitizeText(text);
  if (!cleaned) return null;

  // Check for incoherent text (garbled, too short, or references other products)
  if (cleaned.length < 5) return null;

  const lower = cleaned.toLowerCase();
  for (const marker of CONTAMINATION_MARKERS) {
    if (lower.includes(marker)) return null;
  }

  return cleaned;
}

/**
 * Sanitize a customer voice / frustration / desire field.
 * Returns cleaned text or a fallback.
 */
export function sanitizeCustomerVoice(text: string | null | undefined, fallback = 'Insufficient data — log customer signals to generate insights'): string {
  const cleaned = sanitizeText(text);
  if (!cleaned) return fallback;
  return cleaned;
}

/**
 * Check if text appears to be a leaked prompt artifact.
 */
export function isPromptArtifact(text: string): boolean {
  if (!text) return false;
  const words = text.split(/\s+/).length;
  // Single label/tag shouldn't exceed 80 words
  if (words > 80) return true;
  // Check for common prompt patterns
  const lower = text.toLowerCase();
  if (lower.includes('you are a') || lower.includes('your task is') || lower.includes('return a json')) return true;
  if (lower.includes('respond with') && lower.includes('format')) return true;
  return false;
}
